// Modal de confirmación reutilizable. Reemplaza window.confirm con UI propia
// del admin (tema oscuro/claro, focus management, animación).
//
// Tiene que ser un Dialog de Radix, no un div fijo en el árbol de la app.
// El modal de la oportunidad (y cualquier otro Dialog) deja pointer-events:
// none en document.body, marca el resto con aria-hidden y pinta su portal
// al final del body con el mismo z-index. Un confirm fuera de esa capa no
// se ve o no recibe el clic.
//
// Uso típico:
//   const confirm = useConfirm();
//   ...
//   const ok = await confirm({
//     title: 'Publicar al sitio',
//     description: 'Esto promueve los borradores y dispara el deploy.',
//     confirmLabel: 'Publicar',
//     tone: 'primary',
//   });
//   if (!ok) return;

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Info, Rocket, Trash2 } from 'lucide-react';
import styles from './ConfirmDialog.module.css';

export type ConfirmTone = 'primary' | 'danger' | 'warning' | 'info';

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /** Si true, oculta el botón Cancelar (modo "alerta informativa, OK"). */
  hideCancel?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      const next = { ...opts, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  function handleAnswer(value: boolean) {
    const current = pendingRef.current;
    if (!current) return;
    pendingRef.current = null;
    current.resolve(value);
    setPending(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog.Root
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) handleAnswer(false);
        }}
      >
        {pending && (
          <ConfirmDialog
            {...pending}
            onCancel={() => handleAnswer(false)}
            onConfirm={() => handleAnswer(true)}
          />
        )}
      </Dialog.Root>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}

function ConfirmDialog({
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'primary',
  hideCancel = false,
  onCancel,
  onConfirm,
}: ConfirmOptions & { onCancel: () => void; onConfirm: () => void }) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const Icon =
    tone === 'danger'
      ? Trash2
      : tone === 'warning'
        ? AlertTriangle
        : tone === 'info'
          ? Info
          : Rocket;

  return (
    <Dialog.Portal>
      <Dialog.Overlay className={styles.backdrop} />
      <Dialog.Content
        className={`${styles.dialog} ${styles[`tone__${tone}`]}`}
        {...(description ? {} : { 'aria-describedby': undefined })}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          confirmRef.current?.focus();
        }}
      >
        <div className={styles.icon} aria-hidden>
          <Icon size={22} />
        </div>
        <Dialog.Title className={styles.title}>{title}</Dialog.Title>
        {description ? (
          <Dialog.Description asChild>
            <div className={styles.description}>{description}</div>
          </Dialog.Description>
        ) : null}
        <div className={styles.actions}>
          {!hideCancel && (
            <button type="button" className={styles.cancel} onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          <button ref={confirmRef} type="button" className={styles.confirm} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );
}
